<script setup>
/**
 * FormControlCategoryTitle
 * Recursively builds a breadcrumb string for nested categories
 * by traversing parent IDs.
 */
import {ref, computed, watch, onMounted} from 'vue'
import {useComponentsStore} from '@/stores/components'
import {useItemDataStore} from '@/stores/itemData'

const props = defineProps({
    tableName: {
        type: String,
        required: true
    },
    id: {
        type: Number,
        default: null
    }
})

const componentsStore = useComponentsStore()
const itemDataStore = useItemDataStore()

const title = ref('')

const componentId = computed(() => componentsStore.componentIds[props.tableName])
const nameField = computed(() => componentsStore.nameField(componentId.value))

/**
 * Recursive function to fetch category data and build the string.
 * @param {number} id - The ID of the current category level.
 * @param {boolean} isParent - Whether we are currently looking at a parent.
 */
const buildCategoryTitle = async (id, isParent = false) => {
    if (!id) return

    try {
        const item = await itemDataStore.get({
            tableName: props.tableName,
            id
        })

        const currentLabel = item[nameField.value]

        if (!isParent) {
            // Initial call (leaf node)
            title.value = currentLabel
        } else {
            // Parent call (prepend to breadcrumb)
            title.value = `${currentLabel} > ${title.value}`
        }

        // If there is a parent, recurse upwards
        if (item.parentId) {
            await buildCategoryTitle(item.parentId, true)
        }
    } catch (error) {
        console.error('Error fetching category title path:', error)
    }
}

const init = () => {
    if (props.id) {
        buildCategoryTitle(props.id)
    } else {
        title.value = ''
    }
}

onMounted(() => {
    init()
})

watch(() => props.id, (newId) => {
    if (newId) {
        buildCategoryTitle(newId)
    } else {
        title.value = ''
    }
})
</script>

<template>
    <span>{{ title }}</span>
</template>